import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const CulturalCelebrations = () => {
  const [selectedCulture, setSelectedCulture] = useState('all');

  const culturalCelebrations = [
    {
      id: 1,
      name: "Chinese New Year",
      culture: "chinese",
      date: "February 10, 2024",
      description: "The most important celebration in Chinese culture, marking the beginning of the lunar new year.",
      traditions: [
        "Red envelopes (hongbao) with money",
        "Family reunion dinners",
        "Fireworks and dragon dances",
        "Cleaning homes to sweep away bad luck"
      ],
      giftIdeas: [
        "Red envelopes with money",
        "Gold jewelry",
        "Tea sets",
        "Lucky bamboo plants",
        "Traditional Chinese artwork"
      ],
      etiquette: [
        "Always use both hands when giving or receiving gifts",
        "Avoid white or black wrapping paper",
        "Even numbers are considered lucky",
        "Don\'t give clocks or sharp objects"
      ],
      colors: ["Red", "Gold"],
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=250&fit=crop"
    },
    {
      id: 2,
      name: "Diwali",
      culture: "indian",
      date: "November 12, 2024",
      description: "The Hindu festival of lights celebrating the victory of light over darkness and good over evil.",
      traditions: [
        "Lighting oil lamps (diyas)",
        "Creating rangoli patterns",
        "Sharing sweets and gifts",
        "Fireworks displays"
      ],
      giftIdeas: [
        "Sweets and dry fruits",
        "Gold or silver items",
        "Decorative diyas",
        "Jewelry",
        "Traditional clothing"
      ],
      etiquette: [
        "Bring sweets when visiting",
        "Dress in bright, festive colors",
        "Remove shoes before entering homes",
        "Greet with \'Happy Diwali'"
      ],
      colors: ["Gold", "Orange", "Red"],
      image: "https://images.unsplash.com/photo-1605379399642-870262d3d051?w=400&h=250&fit=crop"
    },
    {
      id: 3,
      name: "Eid al-Fitr",
      culture: "islamic",
      date: "April 10, 2024",
      description: "Islamic celebration marking the end of Ramadan, the holy month of fasting.",
      traditions: [
        "Special prayers at mosque",
        "Giving to charity (Zakat al-Fitr)",
        "Family gatherings and feasts",
        "Wearing new clothes"
      ],
      giftIdeas: [
        "Money for children (Eidi)",
        "New clothes",
        "Dates and sweets",
        "Islamic books",
        "Perfumes and fragrances"
      ],
      etiquette: [
        "Greet with \'Eid Mubarak'",
        "Give money to children",
        "Dress modestly and nicely",
        "Bring food when visiting"
      ],
      colors: ["Green", "Gold", "White"],
      image: "https://images.unsplash.com/photo-1590736969955-71cc94901144?w=400&h=250&fit=crop"
    },
    {
      id: 4,
      name: "Day of the Dead",
      culture: "mexican",
      date: "November 1-2, 2024",
      description: "Mexican celebration honoring deceased loved ones with colorful altars and offerings.",
      traditions: [
        "Building ofrendas (altars)",
        "Decorating with marigolds",
        "Making sugar skulls",
        "Visiting cemeteries"
      ],
      giftIdeas: [
        "Marigold flowers",
        "Sugar skulls",
        "Favorite foods of deceased",
        "Candles and incense",
        "Photos and mementos"
      ],
      etiquette: [
        "It\'s a celebration, not mourning",
        "Bright colors are encouraged",
        "Share stories about the deceased",
        "Participate respectfully if invited"
      ],
      colors: ["Orange", "Purple", "Pink"],
      image: "https://images.unsplash.com/photo-1509557965043-3c4f5b3d1b8a?w=400&h=250&fit=crop"
    },
    {
      id: 5,
      name: "Hanukkah",
      culture: "jewish",
      date: "December 25, 2024 - January 2, 2025",
      description: "Jewish festival of lights commemorating the rededication of the Temple in Jerusalem.",
      traditions: [
        "Lighting the menorah",
        "Playing dreidel games",
        "Eating fried foods",
        "Exchanging gifts for 8 nights"
      ],
      giftIdeas: [
        "Books and educational items",
        "Judaica items",
        "Dreidels and games",
        "Chocolate coins (gelt)",
        "Charitable donations"
      ],
      etiquette: [
        "Gifts are given each night",
        "Focus on meaningful over expensive",
        "Include children in traditions",
        "Greet with \'Happy Hanukkah'"
      ],
      colors: ["Blue", "Silver", "White"],
      image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=250&fit=crop"
    },
    {
      id: 6,
      name: "Kwanzaa",
      culture: "african_american",
      date: "December 26, 2024 - January 1, 2025",
      description: "African-American celebration honoring African heritage and seven core principles.",
      traditions: [
        "Lighting kinara candles",
        "Discussing daily principles",
        "Sharing African foods",
        "Giving meaningful gifts"
      ],
      giftIdeas: [
        "Books about African culture",
        "Handmade crafts",
        "Educational items",
        "African art and textiles",
        "Items that promote learning"
      ],
      etiquette: [
        "Focus on handmade or educational gifts",
        "Learn about the seven principles",
        "Participate in discussions",
        "Greet with \'Happy Kwanzaa'"
      ],
      colors: ["Red", "Black", "Green"],
      image: "https://images.unsplash.com/photo-1609298662063-8d2e2e0b7b8a?w=400&h=250&fit=crop"
    }
  ];

  const cultures = [
    { value: 'all', label: 'All Cultures', icon: 'Globe' },
    { value: 'chinese', label: 'Chinese', icon: 'Star' },
    { value: 'indian', label: 'Indian', icon: 'Flame' },
    { value: 'islamic', label: 'Islamic', icon: 'Moon' },
    { value: 'mexican', label: 'Mexican', icon: 'Flower' },
    { value: 'jewish', label: 'Jewish', icon: 'Star' },
    { value: 'african_american', label: 'African American', icon: 'Heart' }
  ];

  const filteredCelebrations = selectedCulture === 'all' 
    ? culturalCelebrations 
    : culturalCelebrations.filter(celebration => celebration.culture === selectedCulture);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-heading font-bold text-accent-800 mb-3">
          Cultural Celebrations
        </h2>
        <p className="text-lg text-accent-600 max-w-3xl mx-auto">
          Discover diverse gift-giving traditions from around the world and learn the proper etiquette for meaningful cultural celebrations.
        </p>
      </div>

      {/* Culture Filter */}
      <div className="flex flex-wrap justify-center gap-3">
        {cultures.map((culture) => (
          <Button
            key={culture.value}
            variant={selectedCulture === culture.value ? "primary" : "outline"}
            size="sm"
            iconName={culture.icon}
            iconPosition="left"
            onClick={() => setSelectedCulture(culture.value)}
            className="transition-all duration-200"
          >
            {culture.label}
          </Button>
        ))}
      </div>

      {/* Celebrations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filteredCelebrations.map((celebration) => (
          <div
            key={celebration.id}
            className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden hover:shadow-warm transition-all duration-300 hover-lift"
          >
            {/* Image */}
            <div className="relative h-48 overflow-hidden">
              <Image
                src={celebration.image}
                alt={celebration.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4">
                <div className="flex space-x-1">
                  {celebration.colors.map((color, index) => (
                    <div
                      key={index}
                      className={`w-4 h-4 rounded-full border-2 border-white shadow-sm ${
                        color === 'Red' ? 'bg-red-500' :
                        color === 'Gold' ? 'bg-yellow-400' :
                        color === 'Orange' ? 'bg-orange-500' :
                        color === 'Green' ? 'bg-green-500' :
                        color === 'Blue' ? 'bg-blue-500' :
                        color === 'Purple' ? 'bg-purple-500' :
                        color === 'Pink' ? 'bg-pink-500' :
                        color === 'Silver' ? 'bg-gray-300' :
                        color === 'White' ? 'bg-white' :
                        color === 'Black'? 'bg-black' : 'bg-gray-400'
                      }`}
                    ></div>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Header */}
              <div className="mb-4">
                <h3 className="text-xl font-heading font-semibold text-accent-800 mb-1">
                  {celebration.name}
                </h3>
                <p className="text-sm text-primary-600 font-medium mb-2">
                  {celebration.date}
                </p>
                <p className="text-accent-600 text-sm leading-relaxed">
                  {celebration.description}
                </p>
              </div>

              {/* Traditions */}
              <div className="mb-4">
                <h4 className="font-medium text-accent-800 mb-2 flex items-center">
                  <Icon name="Star" size={16} className="mr-2 text-secondary-500" />
                  Key Traditions
                </h4>
                <ul className="space-y-1">
                  {celebration.traditions.slice(0, 3).map((tradition, index) => (
                    <li key={index} className="text-sm text-accent-600 flex items-start">
                      <span className="w-1.5 h-1.5 bg-secondary-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                      {tradition}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Gift Ideas */}
              <div className="mb-4">
                <h4 className="font-medium text-accent-800 mb-2 flex items-center">
                  <Icon name="Gift" size={16} className="mr-2 text-primary-500" />
                  Gift Ideas
                </h4>
                <div className="flex flex-wrap gap-2">
                  {celebration.giftIdeas.slice(0, 4).map((gift, index) => (
                    <span
                      key={index}
                      className="inline-block px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded-full"
                    >
                      {gift}
                    </span>
                  ))}
                </div>
              </div>

              {/* Etiquette Tips */}
              <div className="mb-6">
                <h4 className="font-medium text-accent-800 mb-2 flex items-center">
                  <Icon name="Info" size={16} className="mr-2 text-warning-500" />
                  Etiquette Tips
                </h4>
                <ul className="space-y-1">
                  {celebration.etiquette.slice(0, 2).map((tip, index) => (
                    <li key={index} className="text-sm text-accent-600 flex items-start">
                      <Icon name="CheckCircle" size={12} className="mt-0.5 mr-2 text-success-500 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action */}
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                iconPosition="right"
                className="w-full text-accent-600 hover:text-accent-800"
              >
                Learn More About {celebration.name}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Educational Note */}
      <div className="bg-gradient-to-r from-secondary-50 to-primary-50 rounded-2xl p-6 border border-accent-100">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon name="BookOpen" size={24} className="text-secondary-600" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-accent-800 mb-2">
              Cultural Sensitivity in Gift-Giving
            </h3>
            <p className="text-accent-600 text-sm leading-relaxed mb-4">
              Understanding cultural traditions helps create meaningful connections and shows respect for diverse backgrounds. Always research specific customs and ask questions when in doubt.
            </p>
            <Button
              variant="secondary"
              size="sm"
              iconName="Users"
              iconPosition="left"
              className="shadow-soft"
            >
              Join Cultural Exchange Community
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CulturalCelebrations;